<template>
  <section id="blog" class="writing">
    <div class="container">
      <div class="section-heading">
        <div class="heading-line"/>
        <h2 class="section-title">Writing</h2>

        <!-- Always here, not only once there are more than three. The count
             starts at exactly three, so a conditional link would be missing
             from the panel today and appear later without explanation. -->
        <NuxtLink to="/writing" class="writing__all">View all</NuxtLink>
      </div>
      <p class="section-subtitle">
        Notes on things I've built, broken, and figured out along the way.
      </p>

      <!-- Matches the empty state on /writing. With nothing compiled in, an
           unreachable database leaves this panel with a heading and a blank
           space under it, which reads as a layout fault rather than as a
           panel with nothing to show. -->
      <WritingList
        v-if="latest.length || pending"
        :posts="latest"
        :pending="pending"
        compact
      />
      <p v-else class="writing__empty">Nothing published yet.</p>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

// Three fits the panel in one row at every width the grid steps through, and a
// row of three reads as a selection rather than as everything there is. The
// rest are a click away.
const MAX = 3;

// Writing lives on Medium, X and LinkedIn, so every entry is a link out.
// Nothing is compiled in, so an unreachable database leaves this panel empty
// rather than showing placeholder articles.
const posts = ref([]);
const { load, usePending } = useLiveContent();

// Placeholders rather than an empty panel while the request is in flight.
// Nothing is compiled in any more, so without this the panel says "nothing
// published" for a moment on every cold load, which is not what is happening.
const pending = usePending();

onMounted(async () => {
  posts.value = await load('posts');
});

const when = (post) => new Date(post.published_at || post.date || 0).valueOf() || 0;

// Newest first. The endpoint already orders by date, but pinning reshuffles
// the row below, so the comparison is needed here regardless.
const byNewest = (a, b) => when(b) - when(a);

// Pinned articles first, then the newest of whatever is left. Nothing pinned
// means the panel is simply the three most recent, which is the behaviour
// before any of this and the one to fall back to.
//
// Pins are ordered by date like everything else rather than by when they were
// pinned, so the row still reads chronologically.
const latest = computed(() => {
  const all = [...posts.value];
  const featured = all.filter((post) => post.featured_at).sort(byNewest).slice(0, MAX);

  const taken = new Set(featured.map((post) => post.id));
  const rest = all.filter((post) => !taken.has(post.id)).sort(byNewest);

  return [...featured, ...rest].slice(0, MAX);
});
</script>

<style lang="scss" scoped>
.writing {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 0;
  background: transparent;
  overflow: hidden;

  .container {
    width: 100%;
  }

  // Unpinned below this, so the padding is the only gap between sections.
  @media (max-width: 768px) {
    padding: clamp(3rem, 9vh, 4.5rem) 0;
  }
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

// margin-left rather than space-between on the row, so re-enabling the heading
// line does not push it away from the title.
.writing__all {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  text-decoration: none;
  transition: color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    color: var(--accent);
    text-decoration: none;
  }
}

.section-subtitle {
  text-align: left;
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0 0 clamp(1rem, 2.5vh, 2.25rem);
}

.writing__empty {
  color: var(--muted);
  font-size: 0.9rem;
}

// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }

  .writing__all {
    font-size: 0.72rem;
  }
}
</style>
